import React, { useEffect, useState } from "react";
import { Table, Button, Space, Modal, Form, Input, InputNumber, Card, Select } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const ProductCurrencyManagement = () => {
  const navigate = useNavigate();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchCurrencies();
    fetchCompanies();
  }, []);

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const response = await axios.get("super-admin/product-currencies");
      setCurrencies(response.data);
    } catch (error) {
      toast.error("Failed to fetch product currencies");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get("super-admin/companies");
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const handleCreate = () => {
    setEditingCurrency(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCurrency(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`super-admin/product-currencies/${record.id}`);
      toast.success("Product currency deleted successfully");
      fetchCurrencies();
    } catch (error) {
      toast.error("Failed to delete product currency");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCurrency) {
        await axios.put(`super-admin/product-currencies/${editingCurrency.id}`, values);
        toast.success("Product currency updated successfully");
      } else {
        await axios.post("super-admin/product-currencies", values);
        toast.success("Product currency created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      fetchCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save product currency");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      key: "symbol",
    },
    {
      title: "Conversion Rate",
      dataIndex: "conversion",
      key: "conversion",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      title: "Company",
      dataIndex: ["company", "company_name"],
      key: "company",
      render: (text) => text || "N/A",
    },
    {
      title: "Products Count",
      dataIndex: ["_count", "product"],
      key: "productCount",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate("/super-admin/dashboard")}
        style={{ marginBottom: "16px" }}
      >
        Back to Dashboard
      </Button>
      <Card
        title="Product Currency Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Product Currency
          </Button>
        }
      >
        <Table
          dataSource={currencies}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCurrency ? "Edit Product Currency" : "Create Product Currency"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Currency Name"
            rules={[{ required: true, message: "Please input currency name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="symbol"
            label="Symbol"
            rules={[{ required: true, message: "Please input symbol!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="conversion"
            label="Conversion Rate"
            rules={[{ required: true, message: "Please input conversion rate!" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="company_id"
            label="Company"
            rules={[{ required: true, message: "Please select company!" }]}
          >
            <Select placeholder="Select Company" allowClear>
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.company_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductCurrencyManagement;
